import type { Metro } from '../../types/metro';
import { cn } from '../../lib/utils';
import { statusBgColor, statusTextColor } from '../../lib/map-utils';
import { lensedRatioForMetro, lensedStatusForMetro } from '../../lib/occupation-utils';

interface MetroListItemProps {
  readonly metro: Metro;
  readonly isSelected: boolean;
  readonly onSelect: (id: string) => void;
  /**
   * Active occupational-group chip id (CR-039 review MUST-FIX 1).
   * When set, the row displays the LENSED figures — the selected
   * group's ratio and status colours — so it agrees with the lensed
   * bubble (CR-06) painted for the same metro. Null/omitted → the
   * metro-level figures, unchanged.
   */
  readonly occupationalGroupId?: string | null;
}

/**
 * One row in the floating panel's metro list. Mirrors the prototype's
 * `.metro-item` block (`_archive/map.html` lines 232-257) plus the body
 * template at lines 848-855:
 *
 *   row 1: name (truncate, 13/600/text-1)
 *   row 2: ratio number (12/800/tabular-nums/status-colored)
 *          + h-1 progress bar (bg border-light, fill = status color, width = ratio*50%)
 *   row 3: meta (11/text-4 — credentials/jobs counts)
 *
 * Selected state moves the left-padding from 5 to 17 (compensating for
 * the 3px border-left so content stays aligned).
 *
 * Toggle button so keyboard users get full Tab + Enter/Space affordance
 * with `aria-pressed` announcing the current selection.
 */
export function MetroListItem({
  metro,
  isSelected,
  onSelect,
  occupationalGroupId = null,
}: MetroListItemProps) {
  // CR-039: display figures follow the occupational-group lens (the
  // same helpers MetroBubble/MetroTooltip/MetroPopup use, so the row
  // can never disagree with the bubble beside it). Both helpers fall
  // back to the metro-level figures when no lens is active or the
  // metro doesn't carry the selected group.
  const { ratio } = lensedRatioForMetro(metro, occupationalGroupId);
  const status = lensedStatusForMetro(metro, occupationalGroupId);
  // Width-as-percent: ratio 0 → 0%, ratio 1.0 (parity inflection) → 50%,
  // ratio 2.0 → 100%. Clamp to 100 so high-surplus metros don't overflow.
  const barWidth = Math.min(100, Math.max(0, ratio * 50));

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={() => onSelect(metro.id)}
      className={cn(
        'block w-full border-b border-border-light text-left transition',
        // Padding: prototype is 10px 20px (px-5 py-[10px]). Selected state
        // adds a 3px left border so left padding shrinks to 17px to keep
        // content aligned. Tailwind `border-l-[3px]` doesn't change
        // padding by itself, hence the explicit pl override.
        isSelected
          ? 'border-l-[3px] border-l-blue bg-ice py-[10px] pl-[17px] pr-5'
          : 'border-l-[3px] border-l-transparent bg-white px-5 py-[10px] hover:bg-ice',
      )}
    >
      <div className="mb-[5px] truncate text-[13px] font-semibold text-text-1">
        {metro.name}
      </div>
      {/* Ratio + progress bar. Credentials / projected-jobs numbers
          were removed 2026-07-02 (Georgetown feedback) -- reviewers
          found them cognitively noisy in the sidebar; the popup +
          Detail page already carry those figures broken down per
          occupational group. */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'min-w-[32px] text-[12px] font-extrabold tabular-nums',
            statusTextColor(status),
          )}
        >
          {ratio.toFixed(2)}
        </span>
        <div
          aria-hidden="true"
          className="h-1 flex-1 overflow-hidden rounded-sm bg-border-light"
        >
          <div
            className={cn('h-full rounded-sm', statusBgColor(status))}
            style={{ width: `${barWidth}%` }}
          />
        </div>
      </div>
    </button>
  );
}
